import { useState, useEffect, useCallback } from 'react';

const PHOTOS = [
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/2b8dd92b0867f5984392ac7b2e006247.jpeg',
    alt: 'Беседка с синей крышей и кованым декором — готовый объект',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/245fdde85bbf3ae4c0d3335d42b49827.jpeg',
    alt: 'Беседки из металла с поликарбонатом — образцы производства',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/798f37fff91cdb537706dd61254346d7.jpeg',
    alt: 'Металлические беседки с кованым декором — два варианта',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/1fa85310b2c8baf26f5b6e39dd05ab61.jpeg',
    alt: 'Беседка с красной арочной крышей и кованым декором — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Cozy%20garden%20gazebo%20with%20built-in%20barbecue%20grill%20and%20metal%20frame%20roof%20forged%20decorations%20evening%20lighting%20Russia%20private%20house%20realistic%20photography&width=800&height=600&seq=gaz-work-4&orientation=landscape',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Large%20outdoor%20metal%20gazebo%20with%20storage%20room%20attached%20and%20wooden%20roof%20black%20wrought%20iron%20elements%20installed%20in%20house%20backyard%20Russia%20daytime%20realistic%20photo&width=800&height=600&seq=gaz-work-5&orientation=landscape',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Round%20metal%20gazebo%20with%20ornamental%20wrought%20iron%20pillars%20and%20roof%20installed%20in%20garden%20summer%20daytime%20realistic%20architectural%20photography%20Russia%20natural%20lighting&width=800&height=600&seq=gaz-work-6&orientation=landscape',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Small%20intimate%20pergola%20style%20metal%20gazebo%20with%20climbing%20plants%20decorative%20forged%20elements%20in%20private%20garden%20Russia%20summer%20daylight%20close%20up%20realistic%20photo&width=800&height=600&seq=gaz-work-7&orientation=landscape',
  },
];

export default function VariantsSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const open = (idx: number) => setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + PHOTOS.length) % PHOTOS.length : null));
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % PHOTOS.length : null));
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, goPrev, goNext]);

  const currentPhoto = lightboxIndex !== null ? PHOTOS[lightboxIndex] : null;

  return (
    <>
      <section className="py-16 md:py-20 bg-background-100">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1400px' }}>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Примеры наших работ
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Реальные беседки, которые мы уже построили для&nbsp;наших клиентов
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {PHOTOS.map((photo, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => open(idx)}
                onKeyDown={(e) => { if (e.key === 'Enter') open(idx); }}
                className="relative group rounded-md overflow-hidden bg-background-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <div className="w-full h-44 md:h-52">
                  <img
                    src={photo.src}
                    alt={photo.alt ?? 'Беседка — готовый объект'}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
                    <i className="ri-zoom-in-line text-xl"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={close}
        >
          <button
            onClick={close}
            className="whitespace-nowrap absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="whitespace-nowrap absolute left-3 md:left-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Предыдущее"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="whitespace-nowrap absolute right-3 md:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Следующее"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentPhoto.src}
              alt={currentPhoto.alt ?? 'Беседка — готовый объект'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-background-50/80 text-sm text-center mt-4 max-w-lg">
              {currentPhoto.alt ?? 'Беседка — готовый объект'}
            </p>
            <p className="text-background-50/40 text-xs mt-1">
              {lightboxIndex! + 1} / {PHOTOS.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}